import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  HomeIcon,
  TruckIcon,
  UsersIcon,
  UserIcon,
  CommandLineIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CogIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon,

} from '@heroicons/react/24/outline';
import { companySettingsService } from '../../services/system/company-settings';

const Sidebar = ({ isOpen, onToggle }) => {
  const { t } = useTranslation();
  const { canAccessModule, isSuperAdmin, currentCompany } = useAuth();
  const { isRTL } = useTheme();
  const location = useLocation();
  const [companyLogo, setCompanyLogo] = useState(null);

  // Load company logo
  useEffect(() => {
    const loadCompanyLogo = async () => {
      if (currentCompany?.id) {
        try {
          console.log('Loading company logo for company:', currentCompany.id);
          
          // First try to get logo from current company object (base64 preferred)
          if (currentCompany.logoBase64) {
            console.log('Found base64 logo in currentCompany object (no CORS issues)');
            setCompanyLogo(currentCompany.logoBase64);
            return;
          }
          
          if (currentCompany.logoUrl) {
            console.log('Found URL logo in currentCompany object:', currentCompany.logoUrl);
            setCompanyLogo(currentCompany.logoUrl);
            return;
          }
          
          // Then try to load from company settings
          const settings = await companySettingsService.getCompanySettings(currentCompany.id);
          console.log('Company settings loaded:', settings);
          
          if (settings?.logoBase64) {
            console.log('Found base64 logo in company settings (no CORS issues)');
            setCompanyLogo(settings.logoBase64);
          } else if (settings?.logoUrl) {
            console.log('Found URL logo in company settings:', settings.logoUrl);
            setCompanyLogo(settings.logoUrl);
          } else {
            console.log('No logo found in company settings');
            setCompanyLogo(null);
          }
        } catch (error) {
          console.error('Error loading company logo:', error);
          setCompanyLogo(null);
        }
      } else {
        console.log('No current company, clearing logo');
        setCompanyLogo(null);
      }
    };

    loadCompanyLogo();
  }, [currentCompany?.id, currentCompany?.logoUrl, currentCompany?.logoBase64]);

  // Super Admin navigation items
  const superAdminMenuItems = [
    {
      name: 'System Overview',
      path: '/super-admin/dashboard',
      icon: HomeIcon,
      module: 'system',
    },
    {
      name: 'Companies',
      path: '/super-admin/companies',
      icon: BuildingOfficeIcon,
      module: 'companies',
    },
    {
      name: 'Analytics',
      path: '/super-admin/analytics',
      icon: ChartBarIcon,
      module: 'system',
    },
    {
      name: 'Billing',
      path: '/super-admin/billing',
      icon: CreditCardIcon,
      module: 'billing',
    },
    {
      name: 'System Users',
      path: '/super-admin/users',
      icon: UserGroupIcon,
      module: 'system',
    },
    {
      name: t('common.settings'),
      path: '/super-admin/settings',
      icon: CogIcon,
      module: 'system',
    },
  ];

  // Regular company navigation items
  const companyMenuItems = [
    {
      name: t('navigation.dashboard'),
      path: '/dashboard',
      icon: HomeIcon,
      module: 'dashboard',
    },
    {
      name: t('navigation.shipments'),
      path: '/shipments',
      icon: TruckIcon,
      module: 'shipments',
    },
    {
      name: t('navigation.clients'),
      path: '/clients',
      icon: UsersIcon,
      module: 'clients',
    },
    {
      name: t('navigation.drivers'),
      path: '/drivers',
      icon: UserIcon,
      module: 'drivers',
    },
    {
      name: t('navigation.vehicles'),
      path: '/vehicles',
      icon: CommandLineIcon,
      module: 'vehicles',
    },
    
    {
      name: t('navigation.accounting'),
      path: '/accounting',
      icon: CurrencyDollarIcon,
      module: 'accounting',
    },
    {
      name: t('accounting.quotations'),
      path: '/quotations',
      icon: CreditCardIcon,
      module: 'quotation',
    },
    {
      name: t('vendors.title'),
      path: '/vendors',
      icon: UsersIcon,
      module: 'vendors',
    },


    {
      name: t('navigation.users'),
      path: '/users',
      icon: UserGroupIcon,
      module: 'users',
    },
    {
      name: t('navigation.settings'),
      path: '/settings',
      icon: CogIcon,
      module: 'settings',
    },
  ];

  const menuItems = isSuperAdmin() ? superAdminMenuItems : companyMenuItems;
  const filteredMenuItems = menuItems.filter(item => canAccessModule(item.module));

  const LogoOrTitle = () => {
    if (companyLogo) {
      return (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
            <img 
              src={companyLogo} 
              alt="Company Logo" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col min-w-0">
            {isSuperAdmin() ? (
              <span className="text-xs text-red-600 font-medium">Super Admin</span>
            ) : currentCompany ? (
              <span className="text-sm font-medium text-gray-700 truncate">{currentCompany.name}</span>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        <h1 className="text-2xl font-bold text-primary-600">Logistiat</h1>
        {isSuperAdmin() ? (
          <span className="text-xs text-red-600 font-medium">Super Admin</span>
        ) : currentCompany ? (
          <span className="text-xs text-gray-500 truncate">{currentCompany.name}</span>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className={`hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:bg-white ${
        isRTL ? 'lg:border-l lg:border-gray-200 lg:right-0' : 'lg:border-r lg:border-gray-200 lg:left-0'
      }`}>
        <div className="flex-1 flex flex-col min-h-0 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <LogoOrTitle />
          </div>
          
          {/* Company switcher for Super Admin */}
          {isSuperAdmin() && currentCompany && (
            <div className="px-4 mt-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-xs font-medium text-blue-800">Viewing as:</div>
                <div className="text-sm font-semibold text-blue-900">{currentCompany.name}</div>
                <div className="text-xs text-blue-600">{currentCompany.plan} plan</div>
              </div>
            </div>
          )}
          
          <nav className="mt-8 flex-1 px-4 space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? `bg-primary-100 text-primary-700 ${
                          isRTL ? 'border-l-2 border-primary-500' : 'border-r-2 border-primary-500'
                        }`
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon
                    className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className={`lg:hidden fixed inset-0 z-30 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onToggle} />
        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white ${
          isRTL ? 'mr-auto' : 'ml-auto'
        }`}>
          <div className={`absolute top-0 ${isRTL ? 'left-0 -ml-12' : 'right-0 -mr-12'} pt-2`}>
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={onToggle}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <LogoOrTitle />
            </div>
            
            {/* Company indicator for mobile */}
            {isSuperAdmin() && currentCompany && (
              <div className="px-4 mt-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-xs font-medium text-blue-800">Viewing as:</div>
                  <div className="text-sm font-semibold text-blue-900">{currentCompany.name}</div>
                </div>
              </div>
            )}
            
            <nav className="mt-8 px-4 space-y-1">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                
                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    onClick={onToggle}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon
                      className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5 flex-shrink-0 ${
                        isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 